/* eslint-disable react/prop-types */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdvancedFilter } from './AdvFilter';
import { DefaultJoinOperator, Filter, FilterColumn, InitalFilter, JoinOperator, TRule } from './filters.types';
import { generateQueryParams } from '@konnecta/generic-table';
import { generateQueryString } from './helpers/queryStringGenerator';
import { JoinOperators } from './JoinOperators';

type FilterInputs = {
  columns: FilterColumn[];
  onApplyFilters: (filters: Filter[], joinOperator: JoinOperator) => void;
  joinOperator: JoinOperator;
  showDebugPanel?: boolean;
  initialFilters?: Filter[];
  key?: string | number; // Add key to force reset
};

export interface QueryFiltersRef {
  addFilter: () => void;
}

const QueryFilters = forwardRef<QueryFiltersRef, FilterInputs>(
  ({ columns, joinOperator: InitjoinOperator, onApplyFilters, showDebugPanel = false, initialFilters = [] }, ref) => {
    const { t } = useTranslation();
    const [filters, setFilters] = useState<Filter[]>(initialFilters);
    const [joinOperator, setJoinOperator] = useState<JoinOperator>(InitjoinOperator);
    const [hasInitialized, setHasInitialized] = useState(false);

    // Sync filters when initialFilters prop changes (e.g., when loading from URL)
    useEffect(() => {
      if (initialFilters.length > 0) {
        setFilters(initialFilters);
        setHasInitialized(true);
      }
    }, [initialFilters]);

    // Sync joinOperator when prop changes
    useEffect(() => {
      setJoinOperator(InitjoinOperator);
    }, [InitjoinOperator]);

    // Add one empty filter on mount if initialFilters is empty
    useEffect(() => {
      if (!hasInitialized && initialFilters.length === 0 && filters.length === 0) {
        const newFilter = InitalFilter();
        setFilters([newFilter]);
        setHasInitialized(true);
      }
    }, [initialFilters.length, filters.length, hasInitialized]);

    const createNewFilter = useCallback(() => {
      const newFilters = [...filters, { ...InitalFilter() }];
      setFilters(newFilters);
      onApplyFilters(newFilters, joinOperator);
    }, [filters, joinOperator, onApplyFilters]);

    // Expose methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        addFilter: createNewFilter,
      }),
      [createNewFilter]
    );

    const updateFilter = useCallback(
      (rules: TRule[], index: number) => {
        let updatedfilters = [...filters];

        const filtersLength = updatedfilters.length;

        //The next statement check the rules and if no more. we remove the remaing filter. (We always so a filter with at least one rule ijnside)
        if (rules.length == 0) {
          if (filtersLength == 1) {
            updatedfilters = [];
          } else {
            updatedfilters.splice(index, 1);
          }
        } else {
          updatedfilters[index].rules = rules;
        }
        setFilters(updatedfilters);

        onApplyFilters(updatedfilters, joinOperator);
      },
      [filters, joinOperator, onApplyFilters]
    );

    const onJoinOperatorChange = useCallback(
      (newJoinOperator: JoinOperator) => {
        // This only changes the top-level join operator between filter groups
        // It does NOT change the join operators within each filter group
        setJoinOperator(newJoinOperator);
        onApplyFilters(filters, newJoinOperator);
      },
      [filters, onApplyFilters]
    );

    const handleFilterJoinOperatorChange = useCallback(
      (newOperator: JoinOperator, filterIndex: number) => {
        setFilters((prevFilters) => {
          const updatedFilters = [...prevFilters];
          updatedFilters[filterIndex].operator = newOperator;
          onApplyFilters(updatedFilters, joinOperator);
          return updatedFilters;
        });
      },
      [joinOperator, onApplyFilters]
    );

    // Generate query string for debug panel
    const queryString = useMemo(() => generateQueryString(filters, joinOperator), [filters, joinOperator]);

    // Generate query parameters for API calls
    const queryParams = useMemo(() => generateQueryParams(filters, joinOperator), [filters, joinOperator]);

    return (
      <div className="flex gap-2 p-2">
        {/* Filters Section - Scrollable */}
        <div className="flex-1 border border-gray-300 rounded-lg p-3">
          <div className="text-sm mb-2 font-semibold">{t('common-filters')}</div>
          {filters.map((filter, idx) => (
            <div className="flex items-center py-2 mb-2" key={filter.id}>
              {filters.length > 1 && (
                <JoinOperators
                  operator={joinOperator}
                  showWhere={idx === 0}
                  onChange={(operator) => onJoinOperatorChange(operator)}
                />
              )}
              <AdvancedFilter
                columns={columns}
                rules={filter.rules}
                onChange={(r) => updateFilter(r, idx)}
                onJoinOperatorChange={(operator) => handleFilterJoinOperatorChange(operator, idx)}
                joinOperator={filter.operator || DefaultJoinOperator}
              />
            </div>
          ))}
        </div>

        {/* Debug Panel - Shows Generated Query */}
        {showDebugPanel && (
          <div className="w-80 border border-gray-300 rounded-lg p-3 bg-gray-50">
            <div className="text-base mb-3 font-semibold">Generated Query</div>
            <div className="bg-white border border-gray-200 rounded p-3 text-sm font-mono whitespace-pre-wrap break-words">
              {queryString}
            </div>

            <div className="mt-4">
              <div className="text-sm font-semibold mb-2">Query Parameters</div>
              <div className="bg-white border border-gray-200 rounded p-3 text-xs font-mono whitespace-pre-wrap break-all">
                {queryParams || 'No query parameters'}
              </div>
            </div>

            {filters.length > 0 && (
              <div className="mt-4">
                <div className="text-sm font-semibold mb-2">Filter Details</div>
                <div className="text-xs space-y-2">
                  <div>
                    <span className="font-medium">Total Filters:</span> {filters.length}
                  </div>
                  <div>
                    <span className="font-medium">Total Rules:</span>{' '}
                    {filters.reduce((sum, f) => sum + (f.rules?.length || 0), 0)}
                  </div>
                  <div>
                    <span className="font-medium">Join Operator:</span> {joinOperator || 'AND'}
                  </div>
                </div>
              </div>
            )}

            {filters.length > 0 && (
              <div className="mt-4">
                <div className="text-sm font-semibold mb-2">Raw JSON</div>
                <pre className="text-xs bg-white border border-gray-200 rounded p-2 overflow-auto max-h-60">
                  {JSON.stringify(filters, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

QueryFilters.displayName = 'QueryFilters';

export default QueryFilters;
